import { atom, type PrimitiveAtom } from 'jotai';

import {
	MAX_TRANSCRIPT_CAPTURE_BYTES,
	MAX_TRANSCRIPT_ITEMS,
	readTranscriptFrame,
	transcriptBytes,
	type TranscriptBuffer,
	type TranscriptKind,
	type TranscriptPayload,
	type TranscriptRecord,
} from './api/transcriptRecords';
import type { BenchmarkArm, BenchmarkEvent } from './api/types';

export type TranscriptItem = {
	id: string;
	kind: TranscriptKind;
	payload: TranscriptPayload;
	progress: string;
	startedAtMs: number;
	completed: boolean;
	incomplete: boolean;
	notice?: string;
	/** An oversized initial snapshot omitted the item's original kind. */
	omissionOnly?: boolean;
};
export type ArmTranscript = {
	session: TranscriptPayload | null;
	prompt: string | null;
	order: readonly string[];
	items: ReadonlyMap<string, TranscriptItem>;
	turn: {
		status: string | null;
		active: boolean;
		startedAtMs: number;
		durationMs: number | null;
	} | null;
	issue: 'invalid' | 'unsupported' | 'limit' | 'expired' | 'access' | null;
};
export type BenchmarkTranscriptState = {
	runId: string | null;
	terminal: boolean;
	lastSequence: number;
	capturedBytes: number;
	pending?: TranscriptBuffer;
	arms: Record<BenchmarkArm, ArmTranscript>;
};
const emptyArm = (): ArmTranscript => ({
	session: null,
	prompt: null,
	order: [],
	items: new Map(),
	turn: null,
	issue: null,
});
export const createTranscriptState = (runId: string | null = null): BenchmarkTranscriptState => ({
	runId,
	terminal: false,
	lastSequence: 0,
	capturedBytes: 0,
	arms: { control: emptyArm(), twg: emptyArm() },
});
export const benchmarkTranscriptAtom: PrimitiveAtom<BenchmarkTranscriptState> =
	atom<BenchmarkTranscriptState>(createTranscriptState());

function applyRecord(arm: ArmTranscript, { frame, payload }: TranscriptRecord): ArmTranscript {
	const time = Date.parse(frame.occurredAt);
	if (frame.kind === 'session') return { ...arm, session: payload };
	if (frame.kind === 'turn') {
		const startedAtMs =
			payload.startedAt != null ? payload.startedAt * 1000 : (arm.turn?.startedAtMs ?? time);
		const completedAtMs = payload.completedAt != null ? payload.completedAt * 1000 : time;
		return {
			...arm,
			turn: {
				status: payload.status ?? arm.turn?.status ?? null,
				active: frame.operation !== 'completed',
				startedAtMs,
				durationMs:
					payload.durationMs ??
					(frame.operation === 'completed' ? Math.max(0, completedAtMs - startedAtMs) : null),
			},
		};
	}

	const previous = arm.items.get(frame.itemId);
	if (!previous && arm.order.length >= MAX_TRANSCRIPT_ITEMS) return { ...arm, issue: 'limit' };
	const items = new Map(arm.items);
	if (frame.kind === 'notice' && frame.truncated && frame.itemId !== 'capture-limit') {
		items.set(frame.itemId, {
			...(previous ?? {
				id: frame.itemId,
				kind: 'notice',
				payload: {},
				progress: '',
				startedAtMs: time,
				completed: false,
				omissionOnly: true,
			}),
			incomplete: true,
			notice: payload.text,
		});
		return { ...arm, items, order: previous ? arm.order : [...arm.order, frame.itemId] };
	}
	if (previous && !previous.omissionOnly && previous.kind !== frame.kind)
		return { ...arm, issue: 'invalid' };
	let item: TranscriptItem = previous?.omissionOnly
		? { ...previous, kind: frame.kind, omissionOnly: false }
		: (previous ?? {
				id: frame.itemId,
				kind: frame.kind,
				payload: {},
				progress: '',
				startedAtMs: time,
				completed: false,
				incomplete: false,
			});
	if (frame.operation === 'append') {
		if (item.completed) return { ...arm, issue: 'invalid' };
		const text = payload.text ?? '';
		if (frame.field === 'summary') {
			const summary = [...(item.payload.summary ?? [])];
			summary[frame.summaryIndex!] = (summary[frame.summaryIndex!] ?? '') + text;
			item = { ...item, payload: { ...item.payload, summary } };
		} else if (frame.field === 'progress') item = { ...item, progress: item.progress + text };
		else if (frame.field === 'text' || frame.field === 'output')
			item = {
				...item,
				payload: { ...item.payload, [frame.field]: (item.payload[frame.field] ?? '') + text },
			};
	} else if (frame.operation === 'replace') item = { ...item, payload };
	else
		item = {
			...item,
			payload: { ...item.payload, ...payload },
			completed: frame.operation === 'completed' || item.completed,
		};
	if (frame.truncated) item = { ...item, incomplete: true };
	items.set(item.id, item);
	return {
		...arm,
		items,
		prompt: item.kind === 'user' ? (item.payload.text ?? arm.prompt) : arm.prompt,
		order: previous ? arm.order : [...arm.order, item.id],
		issue: frame.itemId === 'capture-limit' ? 'limit' : arm.issue,
	};
}
/** A skipped frame is a notice. It must not freeze the lane the way a protocol or memory limit does. */
function noteGap(arm: ArmTranscript): ArmTranscript {
	if (arm.issue === 'limit' || arm.issue === 'unsupported') return arm;
	return { ...arm, issue: 'invalid' };
}
function settle(arm: ArmTranscript): ArmTranscript {
	return {
		...arm,
		turn: arm.turn ? { ...arm.turn, active: false } : null,
		items: new Map(
			[...arm.items].map(([id, item]) => [
				id,
				item.completed ? item : { ...item, incomplete: true },
			]),
		),
	};
}
export function transcriptUnavailable(
	state: BenchmarkTranscriptState,
	issue: 'expired' | 'invalid' | 'access',
): BenchmarkTranscriptState {
	return {
		...state,
		pending: undefined,
		arms: { control: { ...state.arms.control, issue }, twg: { ...state.arms.twg, issue } },
	};
}
export function applyTranscriptEvent(
	state: BenchmarkTranscriptState,
	event: BenchmarkEvent,
): BenchmarkTranscriptState {
	if (event.name === 'reconnect') return state;
	if (state.runId !== event.data.runId) state = createTranscriptState(event.data.runId);
	if (event.data.sequence <= state.lastSequence) return state;
	let next = { ...state, lastSequence: event.data.sequence };
	if (event.name === 'transcript') {
		const armName = event.data.arm;
		if (armName !== 'control' && armName !== 'twg') return transcriptUnavailable(next, 'invalid');
		const arm = next.arms[armName];
		if (arm.issue === 'limit' || arm.issue === 'unsupported') return next;
		const capturedBytes =
			next.capturedBytes +
			(typeof event.data.content === 'string' ? transcriptBytes(event.data.content) : 0);
		if (capturedBytes > MAX_TRANSCRIPT_CAPTURE_BYTES)
			return {
				...next,
				pending: undefined,
				arms: {
					control: { ...next.arms.control, issue: 'limit' },
					twg: { ...next.arms.twg, issue: 'limit' },
				},
			};
		// One open group for the run. A frame that does not finish it is dropped, then this
		// frame is read on its own. An orphan chunk (index other than 0) is dropped too.
		// A partial group is never rendered.
		// The dropped group's own arm gets the gap notice, which may not be this frame's arm.
		const openGroup = next.pending;
		let read = readTranscriptFrame(event.data, openGroup);
		let arms = next.arms;
		if (read.kind === 'invalid' && openGroup) {
			const droppedArm = openGroup.frame.arm;
			arms = { ...arms, [droppedArm]: noteGap(arms[droppedArm]) };
			read = readTranscriptFrame(event.data, undefined);
		}
		const current = arms[armName];
		const withGap = read.kind === 'invalid' ? noteGap(current) : current;
		next = {
			...next,
			capturedBytes,
			pending: read.kind === 'pending' ? read.buffer : undefined,
		};
		if (read.kind === 'pending') return { ...next, arms: { ...arms, [armName]: withGap } };
		return {
			...next,
			arms: {
				...arms,
				[armName]:
					read.kind === 'record'
						? applyRecord(withGap, read.record)
						: read.kind === 'unsupported'
							? { ...current, issue: 'unsupported' }
							: withGap,
			},
		};
	}
	if (event.name === 'arm' && event.data.status !== 'started')
		return { ...next, arms: { ...next.arms, [event.data.arm]: settle(next.arms[event.data.arm]) } };
	if (event.name === 'result' || event.name === 'error') {
		if (next.pending) {
			const pendingArm = next.pending.frame.arm;
			next = {
				...next,
				pending: undefined,
				arms: { ...next.arms, [pendingArm]: noteGap(next.arms[pendingArm]) },
			};
		}
		return {
			...next,
			terminal: true,
			arms: { control: settle(next.arms.control), twg: settle(next.arms.twg) },
		};
	}
	return next;
}
